import { type ReactNode, useCallback, useEffect, useState } from "react"

import { isApiProblem } from "../../api/problem"
import { changeMyPassword, listMySessions, revokeMyOtherSessions, revokeMySession } from "../../api/profile.api"
import { accessStore } from "../../access/access-store"
import { Badge, Button, TextField } from "../../ui"
import {
  type SessionRow,
  TEXT_OFFLINE,
  TEXT_PASSWORD_CONSEQUENCE,
  TEXT_REVOKE_OTHERS_NOTE,
  passwordFormErrors,
  passwordLine,
  sessionRows,
} from "./profile-model"
import styles from "./profile.module.css"

/**
 * Блоки «Пароль», «Активные сеансы» и «Выйти» — общие для администратора (1-Э1.10)
 * и мастера (5-М7). Без сети правка запрещена: список показывается по последним данным,
 * а кнопки честно выключены с причиной, а не падают ошибкой после нажатия.
 */

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine))
  useEffect(() => {
    const on = (): void => setOnline(true)
    const off = (): void => setOnline(false)
    window.addEventListener("online", on)
    window.addEventListener("offline", off)
    return () => {
      window.removeEventListener("online", on)
      window.removeEventListener("offline", off)
    }
  }, [])
  return online
}

export function PasswordBlock(props: { readonly passwordChangedAt: string | null; readonly onChanged: () => void }): ReactNode {
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [showErrors, setShowErrors] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  const errors = showErrors ? passwordFormErrors(current, next) : null

  const submit = async (): Promise<void> => {
    setShowErrors(true)
    const check = passwordFormErrors(current, next)
    if (check.current !== null || check.next !== null) return
    setBusy(true)
    setProblem(null)
    try {
      await changeMyPassword(current, next)
      setDone(true)
      setOpen(false)
      setCurrent("")
      setNext("")
      setShowErrors(false)
      props.onChanged()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      // Неверный текущий пароль сервер отдаёт как invalid_credentials — говорим прямо.
      setProblem(error.code === "invalid_credentials" ? "Текущий пароль не подошёл" : (error.detail ?? error.title))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>Пароль</span>
        <span className={styles.muted}>{passwordLine(props.passwordChangedAt)}</span>
      </div>
      {done ? <div className={styles.notice}>Пароль изменён. Остальные сеансы закрыты, этот продолжает работать.</div> : null}
      {open ? (
        <div className={styles.form}>
          <TextField
            label="Текущий пароль"
            type="password"
            autoComplete="current-password"
            value={current}
            error={errors?.current ?? undefined}
            onChange={(event) => setCurrent(event.target.value)}
          />
          <TextField
            label="Новый пароль"
            hint="Не короче 10 знаков"
            type="password"
            autoComplete="new-password"
            value={next}
            error={errors?.next ?? undefined}
            onChange={(event) => setNext(event.target.value)}
          />
          {problem === null ? null : (
            <div className={styles.error} role="alert">
              {problem}
            </div>
          )}
          <div className={styles.row}>
            <Button onClick={() => setOpen(false)}>Отмена</Button>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Меняем…"
              deniedReason={online ? undefined : TEXT_OFFLINE}
              onClick={() => void submit()}
            >
              Сменить пароль
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.row}>
          <Button deniedReason={online ? undefined : TEXT_OFFLINE} onClick={() => { setOpen(true); setDone(false) }}>
            Сменить пароль
          </Button>
        </div>
      )}
      <p className={styles.hint}>{TEXT_PASSWORD_CONSEQUENCE}</p>
    </section>
  )
}

export function SessionsBlock(props: { readonly title: string; readonly reloadKey?: number }): ReactNode {
  const online = useOnline()
  const [rows, setRows] = useState<readonly SessionRow[] | null>(null)
  const [loadedAt, setLoadedAt] = useState<Date | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const sessions = await listMySessions(signal)
      setRows(sessionRows(sessions))
      setLoadedAt(new Date())
      setProblem(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load, props.reloadKey])

  const act = async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy(key)
    setProblem(null)
    try {
      await action()
      await load()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(null)
    }
  }

  // Пока список не пришёл, «других входов нет» — неправда: он неизвестен. Закрыть остальные
  // безопасно и при нуле других, поэтому кнопку тогда не выключаем.
  const others = rows === null ? null : rows.filter((row) => !row.current).length

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>{props.title}</span>
        <Button
          size="sm"
          deniedReason={!online ? TEXT_OFFLINE : others === 0 ? "Других входов нет" : undefined}
          loading={busy === "others"}
          onClick={() => void act("others", revokeMyOtherSessions)}
        >
          Завершить все, кроме этого
        </Button>
      </div>
      {rows === null ? (
        problem === null ? (
          <p className={styles.muted}>Загружаем входы…</p>
        ) : (
          <div className={styles.row}>
            <Button
              size="sm"
              onClick={() => {
                setProblem(null)
                void load()
              }}
            >
              Повторить
            </Button>
          </div>
        )
      ) : (
        <ul className={styles.sessions}>
          {rows.map((row) => (
            <li key={row.id} className={styles.session}>
              <span className={styles.sessionMain}>
                <span>{row.device}</span>
                <span className={styles.muted}>{row.sub}</span>
              </span>
              {row.support ? (
                // Ни «Завершить», ни «Скрыть»: это сеанс нашего сотрудника, он кончится
                // сам по сроку, названному в строке. Плашка объясняет, почему кнопки нет.
                <Badge tone="warn">поддержка</Badge>
              ) : row.current ? (
                <Badge tone="ok">текущий</Badge>
              ) : (
                <Button
                  size="sm"
                  deniedReason={online ? undefined : TEXT_OFFLINE}
                  loading={busy === row.id}
                  onClick={() => void act(row.id, () => revokeMySession(row.id))}
                >
                  Завершить
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!online && loadedAt !== null ? (
        <p className={styles.hint}>
          Нет связи. Список входов — по данным на{" "}
          {loadedAt.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}, изменить пароль и завершить
          сеансы сейчас нельзя.
        </p>
      ) : null}
      {problem === null ? null : (
        <div className={styles.error} role="alert">
          {problem}
        </div>
      )}
      <p className={styles.hint}>{TEXT_REVOKE_OTHERS_NOTE}</p>
    </section>
  )
}

export function SignOutBlock(): ReactNode {
  return (
    <div className={styles.row}>
      <Button variant="secondary" onClick={() => void accessStore.signOut()}>
        Выйти из кабинета
      </Button>
    </div>
  )
}
